import { Row, Col, List, Flex } from "antd"
import { Button } from "src/global/ui/Button/Button"
import { theme } from "src/global/ui/themes/theme.ts"
import { MetaNotes } from "src/pages/DataAssets/views/Expectations/Expectation/MetaNotes"
import { renderExpectation } from "src/pages/DataAssets/views/Expectations/Expectation/utils"
import { parseMetaNotes } from "src/pages/DataAssets/views/Expectations/Expectation/utils/parsing.ts"
import { FragmentType, graphql, unmaskFragment } from "src/api/graphql"

export type ExpectationDraftConfigMaskedFragment = FragmentType<typeof expectationDraftConfigMaskedFragment>

export const expectationDraftConfigMaskedFragment = graphql(`
  fragment ExpectationDraftConfigMasked on ExpectationConfiguration {
    geCloudId
    expectationType
    description
    kwargs
    renderedContent {
      name
      valueType
      value {
        ... on GraphType {
          metaNotes
          schema
          __typename
        }
        ... on StringValueType {
          metaNotes
          template
          schema
          params
          codeBlock
          __typename
        }
        ... on TableType {
          metaNotes
          schema
          __typename
        }
      }
    }
  }
`)

type DraftExpectationsListProps = {
  ExpectationDraftConfigIdDraftExpectationTuple: [string, ExpectationDraftConfigMaskedFragment][]
  onApprove?: (id: string) => void
  onReject?: (id: string) => void
  loading?: boolean
}

export const DraftExpectationsList = ({
  ExpectationDraftConfigIdDraftExpectationTuple,
  loading,
  onApprove,
  onReject,
}: DraftExpectationsListProps) => {
  const expectationPairs = ExpectationDraftConfigIdDraftExpectationTuple.map(([id, maskedExpectation]) => ({
    id,
    expectation: maskedExpectation,
  }))

  return (
    <List
      loading={loading}
      style={{ width: "100%" }}
      bordered
      dataSource={expectationPairs}
      renderItem={({ id, expectation }) => {
        const unmaskedExpectation = unmaskFragment(expectationDraftConfigMaskedFragment, expectation)
        const renderedContentItem = unmaskedExpectation?.renderedContent?.[0]
        const rawMetaNotes = renderedContentItem?.value?.metaNotes

        let parsedMetaNotes = null
        try {
          parsedMetaNotes = rawMetaNotes ? parseMetaNotes(rawMetaNotes) : null
        } catch (error) {
          console.error("Failed to parse metaNotes:", error)
        }

        const renderedContent = renderExpectation({
          expectationConfiguration: unmaskedExpectation,
        })

        return (
          <List.Item key={unmaskedExpectation?.geCloudId || id}>
            <Row justify="space-between" wrap={false} style={{ width: "100%" }} align="top">
              <Col>
                {renderedContent}
                {parsedMetaNotes && <MetaNotes metaNotes={parsedMetaNotes} />}
              </Col>
              <Col style={{ marginLeft: theme.spacing.s }}>
                <Flex gap="small" justify="center" align="end">
                  <Button icon="check" type="text" onClick={() => onApprove?.(id)} aria-label="Approve Expectation" />
                  <Button icon="close" type="text" onClick={() => onReject?.(id)} aria-label="Reject Expectation" />
                </Flex>
              </Col>
            </Row>
          </List.Item>
        )
      }}
    />
  )
}
